import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Lock, Mail, User, Phone, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage, translations } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerAuth = () => {
  const { language } = useLanguage();
  const t = translations[language].partnerAuth;
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    organization: "",
    organization_type: "",
    phone: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/partner/login`, loginData, { withCredentials: true });
      toast.success(t.loginSuccess);
      navigate("/partner");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t.loginError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/partner/register`, registerData, { withCredentials: true });
      toast.success(t.registerSuccess);
      navigate("/partner");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t.registerError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-warm-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-warm-700" />
          </div>
          <h1
            className="text-3xl font-semibold text-gray-900 mb-2"
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            data-testid="partner-auth-title"
          >
            {t.title}
          </h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        {/* Toggle */}
        <div className="flex bg-gray-100 rounded-full p-1 mb-6" role="tablist">
          <button
            onClick={() => { setIsLogin(true); setError(""); }}
            className={`flex-1 py-3 rounded-full text-sm font-medium transition-all ${
              isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
            }`}
            role="tab"
            aria-selected={isLogin}
            data-testid="partner-login-tab"
          >
            {t.loginTab}
          </button>
          <button
            onClick={() => { setIsLogin(false); setError(""); }}
            className={`flex-1 py-3 rounded-full text-sm font-medium transition-all ${
              !isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
            }`}
            role="tab"
            aria-selected={!isLogin}
            data-testid="partner-register-tab"
          >
            {t.registerTab}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm mb-4" role="alert" data-testid="partner-auth-error">
            {error}
          </div>
        )}

        {isLogin ? (
          <form onSubmit={handleLogin} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
            <div>
              <Label htmlFor="login-email" className="text-sm font-medium text-gray-700 mb-2 block">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="login-email"
                  type="email"
                  required
                  data-testid="partner-login-email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="pl-10 rounded-xl border-gray-200"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="login-password" className="text-sm font-medium text-gray-700 mb-2 block">
                {t.password}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="login-password"
                  type="password"
                  required
                  data-testid="partner-login-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="pl-10 rounded-xl border-gray-200"
                  placeholder="********"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-full py-5"
              data-testid="partner-login-submit"
            >
              {isSubmitting ? t.loggingIn : t.login}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
            <div>
              <Label htmlFor="reg-name" className="text-sm font-medium text-gray-700 mb-2 block">{t.fullName} *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input id="reg-name" required data-testid="partner-reg-name" value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  className="pl-10 rounded-xl border-gray-200" placeholder={t.fullName} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reg-email" className="text-sm font-medium text-gray-700 mb-2 block">{t.email} *</Label>
                <Input id="reg-email" type="email" required data-testid="partner-reg-email" value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="rounded-xl border-gray-200" placeholder="your@email.com" />
              </div>
              <div>
                <Label htmlFor="reg-phone" className="text-sm font-medium text-gray-700 mb-2 block">{t.phone} *</Label>
                <Input id="reg-phone" type="tel" required data-testid="partner-reg-phone" value={registerData.phone}
                  onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  className="rounded-xl border-gray-200" placeholder="(xxx) xxx-xxxx" />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-org" className="text-sm font-medium text-gray-700 mb-2 block">{t.organization} *</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input id="reg-org" required data-testid="partner-reg-org" value={registerData.organization}
                  onChange={(e) => setRegisterData({ ...registerData, organization: e.target.value })}
                  className="pl-10 rounded-xl border-gray-200" placeholder={t.organization} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">{t.orgType} *</Label>
              <Select value={registerData.organization_type} onValueChange={(v) => setRegisterData({ ...registerData, organization_type: v })}>
                <SelectTrigger data-testid="partner-reg-orgtype" className="rounded-xl border-gray-200">
                  <SelectValue placeholder={t.selectOrgType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="police">{t.orgTypes.police}</SelectItem>
                  <SelectItem value="cps">{t.orgTypes.cps}</SelectItem>
                  <SelectItem value="court">{t.orgTypes.court}</SelectItem>
                  <SelectItem value="hospital">{t.orgTypes.hospital}</SelectItem>
                  <SelectItem value="nonprofit">{t.orgTypes.nonprofit}</SelectItem>
                  <SelectItem value="attorney">{t.orgTypes.attorney}</SelectItem>
                  <SelectItem value="school">{t.orgTypes.school}</SelectItem>
                  <SelectItem value="other">{t.orgTypes.other}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="reg-password" className="text-sm font-medium text-gray-700 mb-2 block">{t.password} *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input id="reg-password" type="password" required minLength={6} data-testid="partner-reg-password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className="pl-10 rounded-xl border-gray-200" placeholder={t.passwordPlaceholder} />
              </div>
            </div>
            <Button
              type="submit"
              disabled={isSubmitting || !registerData.name || !registerData.email || !registerData.organization || !registerData.organization_type || !registerData.password || !registerData.phone}
              className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-full py-5"
              data-testid="partner-register-submit"
            >
              {isSubmitting ? t.registering : t.register}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          <a href="/" className="text-warm-700 hover:text-warm-800">{t.backToSite}</a>
        </p>
      </motion.div>
    </div>
  );
};

export default PartnerAuth;
